import React, { useState, useEffect } from "react";
/* COMPONENTS */
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid, IconButton, Typography, Switch, FormControlLabel, useTheme, CircularProgress } from "@mui/material";
/* LAB */
import { LoadingButton } from '@mui/lab';
/* DATEPICKER */
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
/* TRANSLATE */
import { useTranslation } from "react-i18next";
/* ICONS */
import CloseIcon from '@mui/icons-material/Close';
/* REQUEST */
import { getUserById, deactivateUser } from "utils/Users";

const DeleteUser = ({ open, setOpen, setAlert, setOpenAlert, setContextMenu, reloading, setSelectedRow, row }) => {

    const { t } = useTranslation();
    const theme = useTheme();
    const current = dayjs();
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(false);
    const [value, setValue] = useState(dayjs());
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [activate, setActivate] = useState('');
    const [text, setText] = useState('');


    useEffect(() => {

        const response  = async () => {

            try{

                const { user } = await getUserById(setLoading, row.id);
                let activate = validateActiveStatus(user.start_dtm, user.end_dtm);
                setStartDate(dayjs(user.start_dtm).format('YYYY-MM-DD'));
                setEndDate(user.end_dtm ? user.end_dtm : '3000-01-01');
                setActivate(activate);
                setText(activate === 'Activo' ? 'user_management.table.dialog.deactivate.buttons.deactivate' : 'user_management.table.dialog.deactivate.buttons.activate');

            }catch(error){
                console.error('Error fetching user:', error.message);
                setLoading(false);
            }finally{
                setLoading(false);
            }
        }

        if(open && row )
            response();

    }, [open, row]);

    const validateActiveStatus = (start, end) => {

        try{

            if (!start || !end) {
                return 'Desactivado';
            }

            const currentDate = dayjs();
            const startDate = dayjs(start);
            const endDate = dayjs(end);

            // Si la fecha end es 3000-01-01, verificar solo que la fecha actual sea >= startDate
            if (endDate.year() === 3000 && endDate.month() === 0 && endDate.date() === 1) {
                // Reemplazamos isSameOrAfter por una combinación de isAfter o isSame
                return (currentDate.isAfter(startDate) || currentDate.isSame(startDate, 'day')) ?
                    'Activo' : 'Desactivado';
            }

            // Si la fecha end es igual a la fecha actual, está desactivado
            if (currentDate.isSame(endDate, 'day')) {
                return 'Desactivado';
            }

            // Verificar si la fecha actual está dentro del rango [start, end]
            if (currentDate.isAfter(startDate) || currentDate.isSame(startDate, 'day')) {
                if (currentDate.isBefore(endDate)) {
                    return 'Activo';
                }
            }

            return 'Desactivado';

        } catch(error){
            console.error('Error validating active status:', error.message);
        }

    };

    const clearForm = () => {
        setChecked(false);
        setValue(current);
        setOpen(false);
        setContextMenu(null);
        setStartDate('');
        setEndDate('');
        setActivate('');
        setText('');
        setSelectedRow([]);
    }

    return (
        <Dialog
            open={open}
            onClose={() => clearForm()}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth="xs"
        >
        <DialogTitle>{t('user_management.table.dialog.deactivate.title')}</DialogTitle>
        <IconButton
                aria-label="close"
                onClick={() => clearForm()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
            <CloseIcon />
        </IconButton>
        <DialogContent>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography>{t('user_management.table.dialog.deactivate.user_status')}</Typography>
                </Grid>
                <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                    <FormControlLabel
                        sx={{ marginRight: 0 }}
                        control={
                            <Switch
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                                name="checked"
                                color="primary"
                            />
                        }
                    />
                </Grid>
                {
                    checked && activate !== 'Activo' && (
                        <Grid item xs={12}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    fullWidth
                                    label={t('user_management.table.dialog.deactivate.selecte_activate_date')}
                                    value={value}
                                    onChange={(newValue) => setValue(newValue)}
                                    slotProps={{
                                        textField: () => ({
                                            required: true,
                                            fullWidth: true,
                                        }),
                                    }}
                                    minDate={dayjs()}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )
                }
                <Grid item xs={12}>
                    <div style={{
                        border: `1px solid ${theme.palette.divider}`, // Usa un color de la paleta
                        padding: '1rem',
                        borderRadius: 1,
                        backgroundColor: theme.palette.background.paper, // Fondo según el tema
                    }}>
                        <Typography>{t('user_management.table.dialog.deactivate.current_status')}: {loading ? <CircularProgress size={15} color="inherit"/> : activate }</Typography>
                        <Typography>{t('user_management.table.dialog.deactivate.start_date')}: {loading ? <CircularProgress size={15} color="inherit"/> : startDate}</Typography>
                        <Typography>{t('user_management.table.dialog.deactivate.end_date')}: {loading ? <CircularProgress size={15} color="inherit"/> : endDate}</Typography>
                    </div>
                </Grid>
            </Grid>
        </DialogContent>

        <DialogActions>
            {
                checked &&  (
                    <>
                        <Button variant='outlined' color='primary'>{t('user_management.table.dialog.deactivate.buttons.cancel')}</Button>
                        <LoadingButton
                            variant='contained'
                            color='error'
                            loading={loading}
                            sx={{ height: 35 }}
                            onClick={async () => {
                                try {
                                    setLoading(true);

                                    // Determinar end_dtm basado en el estado actual (checked)
                                    let end_dtm = activate === 'Activo' ? dayjs().format('YYYY-MM-DD') :// Si está activo (checked=true), desactivar con fecha actual
                                    dayjs('3000-01-01').format('YYYY-MM-DD');  // Si está inactivo (checked=false), activar hasta 3000

                                    // Determinar start_dtm
                                    let start_dtm = value ?
                                        value.format('YYYY-MM-DD') :  // Usar fecha seleccionada si existe
                                        dayjs().format('YYYY-MM-DD'); // Usar fecha actual si no hay selección

                                    let body = {
                                        username: row.username,
                                        start_dtm: start_dtm,
                                        end_dtm : end_dtm,
                                        enable: checked ? true : false
                                    }

                                    const response = await deactivateUser(setLoading, body);

                                    if (response.status === 200) {

                                        let activate = validateActiveStatus(start_dtm, end_dtm);
                                        let msg = activate === 'Activo' ? 'user_management.table.dialog.deactivate.messages.activate_success' : 'user_management.table.dialog.deactivate.messages.deactivate_success';
                                        setAlert({ msg: t(msg), severity: 'success' });
                                        setOpenAlert(true);
                                        clearForm();
                                        reloading();
                                    }

                                } catch(error) {
                                    console.error('Error deactivating user:', error.message);
                                    setAlert({
                                        open: true,
                                        message: t('user_management.table.dialog.deactivate.messages.error'),
                                        severity: 'error'
                                    });
                                    setOpenAlert(true);
                                    setLoading(false);
                                }
                            }}
                        >
                            {text ? t(text) : ''}
                        </LoadingButton>

                    </>
                )
            }
        </DialogActions>
        </Dialog>
    )

}

export { DeleteUser };